<template>
    <b-modal
        v-model="modalVisible"
        size="xl"
        header-bg-variant="dark"
        header-text-variant="light"
        no-close-on-backdrop
        no-close-on-esc
        hide-header-close
        >
        <template v-slot:modal-title>
            <div class="pb-2">IMPORTAR PEDIDOS DESDE EXCEL</div>
        </template>
        
        <!-- Paso 1: Subir y validar archivo -->
        <div v-if="currentStep === 'upload'">
            <b-row>
                <b-col cols="12">
                    <p class="font-weight-medium">Seleccione el archivo Excel con los pedidos a importar:</p>
                </b-col>
                <b-col cols="12">
                    <b-form-file
                        v-model="selectedFile"
                        placeholder="Seleccione o arrastre un archivo..."
                        drop-placeholder="Suelte el archivo aquí..."
                        accept=".xlsx, .xls"
                        :state="Boolean(selectedFile)"
                    ></b-form-file>
                    <div class="mt-2">
                        <small class="text-muted">El archivo debe tener el formato establecido.</small>
                    </div>
                </b-col>
            </b-row>
        </div>
        
        <!-- Paso 2: Mostrar resultados de validación y seleccionar órdenes a procesar -->
        <div v-if="currentStep === 'validation'" class="validation-results">
            <b-row>
                <b-col cols="12">
                    <h5>Resultados de Validación</h5>
                    <div class="d-flex flex-wrap mb-3">
                        <div class="mr-4">
                            <strong>Total de Pedidos:</strong> {{ validationResults.totalOrders }}
                        </div>
                        <div class="mr-4">
                            <strong>Con Errores:</strong> {{ validationResults.ordersWithErrors }}
                        </div>
                        <div class="mr-4">
                            <strong>Problemas de Stock:</strong> {{ validationResults.stockIssues }}
                        </div>
                        <div class="mr-4">
                            <strong>Items No Encontrados:</strong> {{ validationResults.itemIssues || 0 }}
                        </div>
                        <div>
                            <strong>Otros Errores:</strong> {{ validationResults.otherErrors }}
                        </div>
                    </div>
                </b-col>
            </b-row>
            
            <!-- Opciones para ignorar problemas -->
            <b-row class="mb-3">
                <!-- Opción para ignorar problemas de stock -->
                <b-col cols="12" v-if="validationResults.canIgnoreStockIssues">
                    <b-form-checkbox v-model="ignoreStockIssues" switch>
                        Ignorar problemas de stock y ajustar cantidades al stock disponible
                    </b-form-checkbox>
                </b-col>
                
                <!-- NUEVO: Opción para ignorar items no encontrados -->
                <b-col cols="12" v-if="validationResults.canIgnoreItemIssues">
                    <b-form-checkbox v-model="ignoreItemIssues" switch class="mt-2">
                        Ignorar items no encontrados o no disponibles (procesar pedidos con al menos 1 item válido)
                    </b-form-checkbox>
                </b-col>
            </b-row>
            
            <!-- Botón para exportar errores a CSV -->
            <b-row class="mb-3">
                <b-col cols="12">
                    <b-button 
                        variant="outline-secondary" 
                        size="sm"
                        @click="exportErrorsToCsv"
                        :disabled="isLoading || validationResults.ordersWithErrors === 0"
                    >
                        <i class="fas fa-file-download mr-1"></i> Exportar Errores a CSV
                    </b-button>
                </b-col>
            </b-row>
            
            <!-- Tabla de órdenes con opción de selección -->
            <b-row>
                <b-col cols="12">
                    <h5>Seleccione los pedidos a procesar:</h5>
                    <div class="d-flex justify-content-end mb-2">
                        <b-button 
                            variant="link" 
                            size="sm" 
                            @click="selectAllValidOrders"
                            class="mr-2"
                        >
                            Seleccionar válidos
                        </b-button>
                        <b-button 
                            variant="link" 
                            size="sm" 
                            @click="deselectAllOrders"
                        >
                            Deseleccionar todos
                        </b-button>
                    </div>
                    
                    <b-table 
                        striped 
                        hover 
                        :items="validationResults.ordersWithStatus" 
                        :fields="orderFields"
                        responsive="sm"
                        class="mt-2"
                        sort-by="rowNumber"
                    >
                        <!-- Columna de selección -->
                        <template v-slot:cell(selected)="data">
                            <b-form-checkbox 
                                v-model="selectedOrders" 
                                :value="data.item.id_internal_order"
                                :disabled="!canSelectOrder(data.item)"
                            ></b-form-checkbox>
                        </template>
                        
                        <!-- Columna de estado -->
                        <template v-slot:cell(status)="data">
                            <span v-if="!data.item.hasErrors" class="badge badge-success">
                                Válido
                            </span>
                            <span v-else-if="data.item.errorType === 'stock_issue'" class="badge badge-warning">
                                Problema de Stock
                            </span>
                            <span v-else-if="data.item.errorType === 'items_not_found'" class="badge badge-info">
                                Items No Encontrados
                            </span>
                            <span v-else class="badge badge-danger">
                                Error
                            </span>
                        </template>
                        
                        <!-- Columna de error -->
                        <template v-slot:cell(error)="data">
                            <span v-if="data.item.hasErrors">
                                {{ data.item.validationResult.message }}
                                
                                <!-- Detalles específicos para problemas de stock -->
                                <div v-if="data.item.errorType === 'stock_issue' && data.item.validationResult.itemsWithStockIssue">
                                    <small v-for="(item, index) in data.item.validationResult.itemsWithStockIssue" :key="index" class="d-block text-muted">
                                        {{ item.name }}: Solicitado: {{ item.requested }}, Disponible: {{ item.available }}
                                    </small>
                                </div>
                                
                                <!-- NUEVO: Detalles específicos para items no encontrados -->
                                <div v-if="data.item.errorType === 'items_not_found' && data.item.validationResult.itemsNotFound">
                                    <small v-for="(item, index) in data.item.validationResult.itemsNotFound" :key="index" class="d-block text-muted">
                                        Código: {{ item.itemCode }}, Cantidad: {{ item.quantity }}
                                    </small>
                                </div>
                            </span>
                            <span v-else class="text-muted">-</span>
                        </template>
                    </b-table>
                </b-col>
            </b-row>
        </div>
        
        <!-- Paso 3: Resultados del procesamiento -->
        <div v-if="currentStep === 'results'" class="processing-results">
            <b-row>
                <b-col cols="12">
                    <h5>Resultados del Procesamiento</h5>
                    <div class="d-flex flex-wrap mb-4">
                        <div class="mr-4">
                            <strong>Procesados:</strong> {{ processingResults.totalProcessed }}
                        </div>
                        <div class="mr-4">
                            <strong>Exitosos:</strong> {{ processingResults.successfulOrders }}
                        </div>
                        <div>
                            <strong>Fallidos:</strong> {{ processingResults.failedOrders }}
                        </div>
                    </div>
                </b-col>
            </b-row>
            
            <!-- Órdenes procesadas exitosamente -->
            <b-row v-if="processingResults.successfulOrders > 0" class="mb-4">
                <b-col cols="12">
                    <h6 class="text-success">Pedidos Procesados Exitosamente</h6>
                    <b-table 
                        striped 
                        hover 
                        :items="processingResults.successDetails" 
                        :fields="successFields"
                        responsive="sm"
                        class="mt-2"
                    >
                        <template v-slot:cell(adjustedForStock)="data">
                            <span v-if="data.item.adjustedForStock" class="badge badge-info">
                                Cantidades ajustadas
                            </span>
                            <span v-else class="text-muted">-</span>
                        </template>
                        
                        <!-- NUEVO: Columna para items filtrados -->
                        <template v-slot:cell(adjustedForItemIssues)="data">
                            <span v-if="data.item.adjustedForItemIssues" class="badge badge-warning">
                                Items filtrados
                            </span>
                            <span v-else class="text-muted">-</span>
                        </template>
                        
                        <!-- NUEVA: Columna para mostrar items procesados vs solicitados -->
                        <template v-slot:cell(itemsInfo)="data">
                            <span v-if="data.item.processedItems && data.item.totalRequestedItems">
                                {{ data.item.processedItems }} / {{ data.item.totalRequestedItems }}
                                <small v-if="data.item.processedItems < data.item.totalRequestedItems" class="text-muted d-block">
                                    ({{ data.item.totalRequestedItems - data.item.processedItems }} items omitidos)
                                </small>
                            </span>
                            <span v-else class="text-muted">-</span>
                        </template>
                    </b-table>
                </b-col>
            </b-row>
            
            <!-- Órdenes con errores -->
            <b-row v-if="processingResults.failedOrders > 0">
                <b-col cols="12">
                    <h6 class="text-danger">Pedidos No Procesados</h6>
                    <b-table 
                        striped 
                        hover 
                        :items="processingResults.failureDetails" 
                        :fields="failureFields"
                        responsive="sm"
                        class="mt-2"
                    >
                        <template v-slot:cell(reason)="data">
                            <span v-html="formatErrorMessage(data.item.reason)"></span>
                        </template>
                    </b-table>
                </b-col>
            </b-row>
        </div>
        
        <template v-slot:modal-footer>
            <!-- Botones para paso de carga -->
            <div v-if="currentStep === 'upload'" class="d-flex w-100 justify-content-between">
                <b-button variant="secondary" @click.prevent="closeModal">
                    Cancelar
                </b-button>
                <b-button 
                    variant="primary" 
                    @click.prevent="validateExcel"
                    :disabled="!selectedFile || isLoading"
                >
                    <b-spinner small v-if="isLoading"></b-spinner>
                    {{ isLoading ? 'Validando...' : 'Validar Pedidos' }}
                </b-button>
            </div>
            
            <!-- Botones para paso de validación -->
            <div v-if="currentStep === 'validation'" class="d-flex w-100 justify-content-between">
                <div>
                    <b-button variant="secondary" @click.prevent="currentStep = 'upload'">
                        Volver
                    </b-button>
                </div>
                <div>
                    <b-button variant="secondary" @click.prevent="closeModal" class="mr-2">
                        Cancelar
                    </b-button>
                    <b-button 
                        variant="primary" 
                        @click.prevent="processSelectedOrders"
                        :disabled="isLoading || selectedOrders.length === 0"
                    >
                        <b-spinner small v-if="isLoading"></b-spinner>
                        {{ isLoading ? 'Procesando...' : `Procesar ${selectedOrders.length} Pedidos` }}
                    </b-button>
                </div>
            </div>
            
            <!-- Botones para paso de resultados -->
            <div v-if="currentStep === 'results'" class="d-flex w-100 justify-content-between">
                <b-button variant="secondary" @click.prevent="closeModal">
                    Cerrar
                </b-button>
                <b-button variant="primary" @click.prevent="resetAndUploadNew">
                    Importar Más Pedidos
                </b-button>
            </div>
        </template>
    </b-modal>
</template>

<script>
export default {
    name: "ExcelUploadModal",
    props: {
        modalVisible: {
            type: Boolean,
            required: true
        },
        isLoading: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            currentStep: 'upload',
            selectedFile: null,
            importId: null,
            validationResults: null,
            ignoreStockIssues: false,
            ignoreItemIssues: false, // NUEVO
            selectedOrders: [],
            processingResults: null,
            
            orderFields: [
                { key: 'selected', label: '' },
                { key: 'rowNumber', label: 'Fila' },
                { key: 'id_internal_order', label: 'ID Interno' },
                { key: 'customer.name', label: 'Cliente' },
                { key: 'status', label: 'Estado' },
                { key: 'error', label: 'Error/Detalle' }
            ],
            successFields: [
                { key: 'rowNumber', label: 'Fila' },
                { key: 'id_internal_order', label: 'ID Interno' },
                { key: 'orderId', label: 'ID Pedido Generado' },
                { key: 'customerName', label: 'Cliente' },
                { key: 'itemsInfo', label: 'Items (Procesados/Solicitados)' }, // NUEVO
                { key: 'adjustedForStock', label: 'Ajustes Stock' },
                { key: 'adjustedForItemIssues', label: 'Items Filtrados' } // NUEVO
            ],
            failureFields: [
                { key: 'rowNumber', label: 'Fila' },
                { key: 'id_internal_order', label: 'ID Interno' },
                { key: 'reason', label: 'Error' }
            ]
        }
    },
    methods: {
        // Validar el archivo Excel (paso 1)
        validateExcel() {
            if (!this.selectedFile) {
                this.showToast('Por favor seleccione un archivo Excel para subir', 'Error', 'danger');
                return;
            }
            
            this.$emit('update:isLoading', true);
            
            // Crear FormData
            const formData = new FormData();
            formData.append('excel_file', this.selectedFile);
            
            // Hacer la petición al nuevo endpoint de validación
            this.$conectar.post('pedidos/import/validate-orders', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
            .then(response => {
                this.validationResults = response.data;
                this.importId = response.data.importId;
                
                // Pre-seleccionar órdenes sin errores
                this.selectedOrders = this.validationResults.ordersWithStatus
                    .filter(order => !order.hasErrors)
                    .map(order => order.id_internal_order);
                
                // Pasar al siguiente paso
                this.currentStep = 'validation';
                
                this.$emit('update:isLoading', false);
            })
            .catch(error => {
                console.error('Error validando archivo:', error);
                const errorMessage = error.response?.data?.message || 'Error al validar el archivo';
                this.showToast(errorMessage, 'Error', 'danger');
                this.$emit('update:isLoading', false);
            });
        },
        
        // Procesar órdenes seleccionadas (paso 2)
        processSelectedOrders() {
            if (this.selectedOrders.length === 0) {
                this.showToast('Por favor seleccione al menos un pedido para procesar', 'Atención', 'warning');
                return;
            }
            
            this.$emit('update:isLoading', true);
            
            // Preparar datos para enviar - MODIFICADO
            const requestData = {
                importId: this.importId,
                ignoreStockIssues: this.ignoreStockIssues,
                ignoreItemIssues: this.ignoreItemIssues, // NUEVO
                ordersToProcess: this.selectedOrders
            };
            
            // Hacer la petición al endpoint de procesamiento
            this.$conectar.post('pedidos/import/process-validated-orders', requestData)
            .then(response => {
                this.processingResults = response.data;
                
                // Pasar al siguiente paso
                this.currentStep = 'results';
                
                // Si hay órdenes procesadas correctamente, notificar al componente padre
                if (response.data.successfulOrders > 0) {
                    this.$emit('orders-processed', response.data.successfulOrders);
                }
                
                this.$emit('update:isLoading', false);
            })
            .catch(error => {
                console.error('Error procesando órdenes:', error);
                const errorMessage = error.response?.data?.message || 'Error al procesar las órdenes';
                this.showToast(errorMessage, 'Error', 'danger');
                this.$emit('update:isLoading', false);
            });
        },
        
        // NUEVO: Determinar si una orden puede ser seleccionada
        canSelectOrder(order) {
            if (!order.hasErrors) {
                return true; // Órdenes sin errores siempre pueden ser seleccionadas
            }
            
            // Órdenes con problemas de stock pueden ser seleccionadas si ignoramos stock
            if (order.errorType === 'stock_issue' && this.ignoreStockIssues) {
                return true;
            }
            
            // Órdenes con items no encontrados pueden ser seleccionadas si ignoramos item issues
            if (order.errorType === 'items_not_found' && this.ignoreItemIssues) {
                return true;
            }
            
            return false; // Otros tipos de error no pueden ser seleccionados
        },
        
        // NUEVO: Método unificado para actualizar selección de órdenes
        updateOrderSelection() {
            if (!this.validationResults || !this.validationResults.ordersWithStatus) {
                return;
            }
            
            // Filtrar órdenes actualmente seleccionadas que ya no pueden ser seleccionadas
            this.selectedOrders = this.selectedOrders.filter(orderId => {
                const order = this.validationResults.ordersWithStatus.find(o => o.id_internal_order === orderId);
                return order && this.canSelectOrder(order);
            });
            
            // Agregar nuevas órdenes que ahora pueden ser seleccionadas
            const newSelectableOrders = this.validationResults.ordersWithStatus
                .filter(order => {
                    // Solo agregar si no está ya seleccionada y puede ser seleccionada
                    return !this.selectedOrders.includes(order.id_internal_order) && 
                           this.canSelectOrder(order);
                })
                .map(order => order.id_internal_order);
            
            this.selectedOrders = [...this.selectedOrders, ...newSelectableOrders];
        },
        
        // Exportar errores a CSV
        exportErrorsToCsv() {
            if (!this.importId) {
                return;
            }
            
            this.$emit('update:isLoading', true);
            
            // Hacer la petición de descarga
            this.$conectar.get(`pedidos/import/export-errors-csv?importId=${this.importId}`, {
                responseType: 'blob'
            })
            .then(response => {
                // Crear link de descarga
                const blob = new Blob([response.data], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                
                // Generar nombre de archivo
                const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
                link.setAttribute('download', `errores_importacion_${date}.csv`);
                
                // Descargar archivo
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                this.$emit('update:isLoading', false);
            })
            .catch(error => {
                console.error('Error exportando errores:', error);
                this.showToast('Error al exportar errores a CSV', 'Error', 'danger');
                this.$emit('update:isLoading', false);
            });
        },
        
        // Seleccionar todas las órdenes válidas - MODIFICADO
        selectAllValidOrders() {
            if (!this.validationResults || !this.validationResults.ordersWithStatus) {
                return;
            }
            
            this.selectedOrders = this.validationResults.ordersWithStatus
                .filter(order => this.canSelectOrder(order))
                .map(order => order.id_internal_order);
        },
        
        // Deseleccionar todas las órdenes
        deselectAllOrders() {
            this.selectedOrders = [];
        },
        
        // Reiniciar y cargar nuevo archivo
        resetAndUploadNew() {
            this.currentStep = 'upload';
            this.selectedFile = null;
            this.importId = null;
            this.validationResults = null;
            this.ignoreStockIssues = false;
            this.ignoreItemIssues = false; // NUEVO
            this.selectedOrders = [];
            this.processingResults = null;
        },
        
        // Cerrar modal
        closeModal() {
            this.resetAndUploadNew();
            this.$emit('close-modal');
        },
        
        // Mostrar notificación
        showToast(message, title, variant) {
            this.$bvToast.toast(message, {
                title: title,
                variant: variant,
                solid: true
            });
        },
        
        // Formatear mensaje de error
        formatErrorMessage(message) {
            if (typeof message === 'string') {
                return message;
            }
            
            try {
                return JSON.stringify(message);
            } catch (e) {
                return 'Error desconocido';
            }
        },
        
        // Compatibilidad con la implementación anterior para mostrar errores
        setErrors(errorDetails) {
            // No es necesario en el nuevo flujo, pero mantenemos compatibilidad
            console.log('setErrors método deprecado en el nuevo flujo', errorDetails);
        }
    },
    watch: {
        // Actualizar selección cuando cambia la opción de ignorar problemas de stock
        ignoreStockIssues() {
            this.updateOrderSelection();
        },
        
        // NUEVO: Actualizar selección cuando cambia la opción de ignorar items no encontrados
        ignoreItemIssues() {
            this.updateOrderSelection();
        },
        
        // Reiniciar estado cuando se abre el modal
        modalVisible(newVal) {
            if (newVal) {
                this.resetAndUploadNew();
            }
        }
    }
};
</script>

<style scoped>
.validation-results, .processing-results {
    max-height: 70vh;
    overflow-y: auto;
}
</style>